import React from 'react';
import { Monitor, Code, Settings, Search, ShoppingCart, Smartphone, ArrowRight, CheckCircle } from 'lucide-react';

export const Services: React.FC = () => {
  const services = [
    {
      icon: <Monitor className="h-12 w-12 text-primary-600" />,
      title: "Web Design",
      description: "Custom, responsive designs that capture your brand essence and engage your audience.",
      features: [
        "Custom Design Concepts",
        "Responsive Mobile Design",
        "User Experience (UX) Design",
        "Brand Integration",
        "Wireframing & Prototyping",
        "Design System Creation"
      ]
    },
    {
      icon: <Code className="h-12 w-12 text-primary-600" />,
      title: "Web Development",
      description: "Robust, scalable websites built with the latest technologies and best practices.",
      features: [
        "Custom WordPress Development",
        "React/Next.js Applications",
        "E-commerce Integration",
        "Content Management Systems",
        "API Development & Integration",
        "Performance Optimization"
      ]
    },
    {
      icon: <Settings className="h-12 w-12 text-primary-600" />,
      title: "Website Management",
      description: "Ongoing maintenance, updates, and optimization to keep your website running smoothly.",
      features: [
        "Regular Content Updates",
        "Security Monitoring & Updates",
        "Performance Monitoring",
        "Backup Management",
        "Technical Support",
        "Analytics Reporting"
      ]
    },
    {
      icon: <Search className="h-12 w-12 text-primary-600" />,
      title: "SEO Services",
      description: "Search engine optimization to improve your visibility and drive organic traffic.",
      features: [
        "Keyword Research & Strategy",
        "On-Page Optimization",
        "Technical SEO Audit",
        "Content Optimization",
        "Local SEO Setup",
        "Monthly Performance Reports"
      ]
    },
    {
      icon: <ShoppingCart className="h-12 w-12 text-primary-600" />,
      title: "E-commerce Solutions",
      description: "Complete online store setup with payment processing and inventory management.",
      features: [
        "WooCommerce/Shopify Setup",
        "Payment Gateway Integration",
        "Inventory Management",
        "Order Processing System",
        "Customer Account Portal",
        "Marketing Tools Integration"
      ]
    },
    {
      icon: <Smartphone className="h-12 w-12 text-primary-600" />,
      title: "Mobile Optimization",
      description: "Ensure your website looks and performs perfectly on all mobile devices.",
      features: [
        "Mobile-First Design",
        "Touch-Friendly Interface",
        "Fast Loading Times",
        "Cross-Device Testing",
        "Progressive Web App (PWA)",
        "App Store Optimization"
      ]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We start by understanding your business, goals, and target audience to create a comprehensive project plan."
    },
    {
      step: "02",
      title: "Design & Prototyping",
      description: "Our designers create wireframes and visual mockups that bring your vision to life before development begins."
    },
    {
      step: "03",
      title: "Development & Testing",
      description: "Our developers build your website using best practices, followed by thorough testing across all devices and browsers."
    },
    {
      step: "04",
      title: "Launch & Optimization",
      description: "We launch your website and provide ongoing optimization to ensure peak performance and user experience."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-32 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Comprehensive web solutions designed to help your business succeed online.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From design to deployment and beyond, we provide end-to-end web solutions tailored to your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card p-8 group">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="transform group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <a
                  href="/get-started"
                  className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a proven process to ensure your project is delivered on time, on budget, and exceeds expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our services and process.
            </p>
          </div>

          <div className="space-y-8">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How long does it take to build a website?
              </h3>
              <p className="text-gray-600">
                Timeline varies based on complexity. A basic website typically takes 2-4 weeks, while complex 
                e-commerce or custom applications can take 6-12 weeks. We'll provide a detailed timeline 
                during our initial consultation.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you provide ongoing support after launch?
              </h3>
              <p className="text-gray-600">
                Yes! We offer comprehensive maintenance packages that include security updates, content updates, 
                performance monitoring, and technical support. We're here to help your website continue 
                performing at its best.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Can you help improve my existing website?
              </h3>
              <p className="text-gray-600">
                Absolutely! We can redesign existing websites, improve performance, add new features, 
                or provide ongoing maintenance and optimization services for websites built by other agencies.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Do you work with small businesses?
              </h3>
              <p className="text-gray-600">
                Yes, we work with businesses of all sizes. We offer scalable solutions and flexible 
                pricing options to accommodate different budgets and requirements.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How do you determine pricing for projects?
              </h3>
              <p className="text-gray-600">
                We provide custom quotes based on your specific requirements, project scope, and timeline. 
                During our consultation, we'll discuss your needs and provide a detailed proposal with 
                transparent pricing.
              </p>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What's included in your monthly maintenance packages?
              </h3>
              <p className="text-gray-600">
                Our maintenance packages include security updates, content updates, performance monitoring, 
                backup management, technical support, and up to 5 hours of changes per month. We also 
                provide detailed analytics reports.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Let's discuss your project and create a custom solution that fits your needs and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/get-started" className="btn-secondary text-lg px-8 py-4">
              Get Free Consultation
            </a>
            <a href="/portfolio" className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600">
              View Our Work
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};